import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";

interface CategoryData {
  name: string;
  slug: string;
  description: string;
  meta_title: string;
  meta_description: string;
  meta_keywords: string;
  icon?: string;
}

const EditCategory = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const baseUrl = import.meta.env.VITE_API_BASE_URL;

  const [formData, setFormData] = useState<CategoryData>({
    name: "",
    slug: "",
    description: "",
    meta_title: "",
    meta_description: "",
    meta_keywords: "",
    icon: "",
  });

  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(true);

  // ✅ Fetch existing category
  useEffect(() => {
    const fetchCategory = async () => {
      try {
        const res = await axios.get(`${baseUrl}/category/${id}`);
        setFormData(res.data.data);
      } catch (err: any) {
        console.error("Error fetching category:", err.response?.data || err.message);
        alert("Failed to fetch category!");
      } finally {
        setLoading(false);
      }
    };
    fetchCategory();
  }, [id, baseUrl]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (name === "name") toSeoUrl(value);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) setFile(e.target.files[0]);
  };

  const toSeoUrl = (url: string) => {
    const weburl = url
      .toString()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/\s+/g, "-")
      .toLowerCase()
      .replace(/&/g, "-and-")
      .replace(/[^a-z0-9\-]/g, "")
      .replace(/-+/g, "-")
      .replace(/^-*/, "")
      .replace(/-*$/, "");
    setFormData((prev) => ({ ...prev, slug: weburl }));
  };

  // ✅ Submit updated category
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const data = new FormData();
    Object.entries(formData).forEach(([key, value]) => {
      if (value) data.append(key, value as any);
    });
    if (file) data.append("image", file);

    try {
      await axios.put(`${baseUrl}/category/${id}`, data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      alert("✅ Category updated successfully!");
      navigate("/settings/category");
    } catch (err: any) {
      console.error("Error updating category:", err.response?.data || err.message);
      alert(err.response?.data?.message || "Failed to update category!");
    }
  };

  if (loading) return <div className="p-5 text-center">Loading...</div>;

  return (
    <div className="main-container container-fluid">
      <div className="page-header">
        <h1 className="page-title">Edit Category</h1>
      </div>

      <form className="card" onSubmit={handleSubmit} encType="multipart/form-data">
        <div className="card-body">
          <div className="form-group">
            <label className="form-label">Category Name</label>
            <input
              type="text"
              className="form-control"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Slug</label>
            <input
              type="text"
              className="form-control"
              name="slug"
              value={formData.slug}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Description</label>
            <textarea
              className="form-control"
              name="description"
              value={formData.description}
              onChange={handleChange}
            ></textarea>
          </div>

          <div className="form-group">
            <label className="form-label">Category Icon</label>
            <input
              type="file"
              className="form-control"
              accept="image/*"
              onChange={handleFileChange}
            />
            {formData.icon && (
              <div className="mt-2">
                <img src={`${baseUrl}${formData.icon}`} alt="icon" width={50} />
              </div>
            )}
          </div>

          <div className="form-group">
            <label className="form-label">Meta Title</label>
            <input
              type="text"
              className="form-control"
              name="meta_title"
              value={formData.meta_title}
              onChange={handleChange}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Meta Description</label>
            <textarea
              className="form-control"
              name="meta_description"
              value={formData.meta_description}
              onChange={handleChange}
            ></textarea>
          </div>

          <div className="form-group">
            <label className="form-label">Meta Keywords</label>
            <textarea
              className="form-control"
              name="meta_keywords"
              value={formData.meta_keywords}
              onChange={handleChange}
            ></textarea>
          </div>

          <button type="submit" className="btn btn-primary">
            Update
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditCategory;
